/**
 * RoleTable Component
 *
 * Displays roles in a table format with view, edit, and delete actions.
 * Shows role name, description, permission count, and assignment count.
 */

import React, { useMemo } from "react";
import { EyeIcon, UserPlusIcon, DocumentDuplicateIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { CopyableCode } from "../../../components/ui/CopyableCode";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import { RoleType, type RoleSanitized } from "../../../types/role.types";

interface RoleTableProps {
  roles: RoleSanitized[];
  isLoading?: boolean;
  onViewRole: (role: RoleSanitized) => void;
  onEditRole: (role: RoleSanitized) => void;
  onDeleteRole: (role: RoleSanitized) => void;
  onAssignRole: (role: RoleSanitized) => void;
  onCopyRole?: (role: RoleSanitized) => void;
  canEdit?: boolean;
  canCreate?: boolean;
  canDelete?: boolean;
}

export const RoleTable: React.FC<RoleTableProps> = ({
  roles,
  isLoading,
  onViewRole,
  onEditRole,
  onDeleteRole,
  onAssignRole,
  onCopyRole,
  canEdit,
  canCreate,
  canDelete,
}) => {
  // Group roles by type (tenant first, then application, then app domain)
  // Must be called before any early returns to satisfy React hooks rules
  const groupedRoles = useMemo(() => {
    const sortByPathThenName = (a: RoleSanitized, b: RoleSanitized) => {
      const pathCmp = (a.path || "/").localeCompare(b.path || "/");
      if (pathCmp !== 0) return pathCmp;
      return (a.roleName || "").localeCompare(b.roleName || "");
    };
    const tenantRoles = roles.filter((role) => role.type === RoleType.TENANT).sort(sortByPathThenName);
    const applicationRoles = roles.filter((role) => role.type === RoleType.APPLICATION).sort(sortByPathThenName);
    const appDomainRoles = roles.filter((role) => role.type === RoleType.APP_DOMAIN).sort(sortByPathThenName);
    return { tenantRoles, applicationRoles, appDomainRoles };
  }, [roles]);

  // Count total assignments (users + groups + applications + service accounts)
  const getAssignmentCount = (role: RoleSanitized): number => {
    return (role.users?.length || 0) + (role.groups?.length || 0) + (role.applications?.length || 0) + (role.serviceAccounts?.length || 0);
  };

  if (isLoading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (roles.length === 0) {
    return (
      <EmptyState
        title="No roles found"
        description="Create your first role to get started."
      />
    );
  }

  // Render a single role row
  const renderRoleRow = (role: RoleSanitized) => {
    const assignmentCount = getAssignmentCount(role);
    const rolePath = role.path || "/";
    const pathDepth = rolePath === "/" ? 0 : rolePath.split("/").filter(Boolean).length;

    return (
      <tr key={role.roleId} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center" style={{ paddingLeft: `${pathDepth * 20}px` }}>
            <div>
              <div className="text-sm font-medium text-gray-900">{role.roleName}</div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <CopyableCode value={role.roleCode} />
        </td>
        <td className="px-6 py-4">
          <div className="text-sm text-gray-900 max-w-xs truncate">
            {role.description || <span className="text-gray-400 italic">No description</span>}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <Badge variant="info">
            {role.permissions.length} permission{role.permissions.length !== 1 ? "s" : ""}
          </Badge>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex gap-2 flex-wrap">
            {/* Show applications for APPLICATION-type roles */}
            {role.type === RoleType.APPLICATION && role.applications && role.applications.length > 0 && (
              <Badge variant="info">
                {role.applications.length} app{role.applications.length !== 1 ? "s" : ""}
              </Badge>
            )}
            {/* Show users for non-APPLICATION roles */}
            {role.type !== RoleType.APPLICATION && role.users && role.users.length > 0 && (
              <Badge variant="success">
                {role.users.length} user{role.users.length !== 1 ? "s" : ""}
              </Badge>
            )}
            {/* Show groups for non-APPLICATION roles */}
            {role.type !== RoleType.APPLICATION && role.groups && role.groups.length > 0 && (
              <Badge variant="warning">
                {role.groups.length} group{role.groups.length !== 1 ? "s" : ""}
              </Badge>
            )}
            {/* Show service accounts for APP_DOMAIN roles only */}
            {role.type === RoleType.APP_DOMAIN && role.serviceAccounts && role.serviceAccounts.length > 0 && (
              <Badge variant="neutral">
                {role.serviceAccounts.length} service account{role.serviceAccounts.length !== 1 ? "s" : ""}
              </Badge>
            )}
            {assignmentCount === 0 && <span className="text-sm text-gray-400 italic">Unassigned</span>}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => onViewRole(role)} title="View Details">
              <EyeIcon className="h-4 w-4" />
            </Button>
            {canEdit && (
              <>
                <Button variant="secondary" size="sm" onClick={() => onAssignRole(role)} title="Assign Role">
                  <UserPlusIcon className="h-4 w-4" />
                </Button>
                {canCreate && onCopyRole && (
                  <Button variant="secondary" size="sm" onClick={() => onCopyRole(role)} title="Copy Role">
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="secondary" size="sm" onClick={() => onEditRole(role)} title="Edit Role">
                  <PencilIcon className="h-4 w-4" />
                </Button>
              </>
            )}
            {canDelete && (
              <Button variant="danger" size="sm" onClick={() => onDeleteRole(role)} title="Delete Role">
                <TrashIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // Render a path group header row
  const renderPathHeader = (path: string) => {
    return (
      <tr key={`path-${path}`} className="bg-gray-50">
        <td colSpan={6} className="px-6 py-1.5">
          <div>
            <span className="text-xs text-gray-500 font-mono">{path}</span>
          </div>
        </td>
      </tr>
    );
  };

  // Render roles with path group headers inserted between groups
  const renderRolesWithPathHeaders = (sortedRoles: RoleSanitized[]) => {
    const rows: React.ReactNode[] = [];
    let lastPath: string | null = null;
    for (const role of sortedRoles) {
      const rolePath = role.path || "/";
      if (rolePath !== lastPath) {
        if (rolePath !== "/") {
          rows.push(renderPathHeader(rolePath));
        }
        lastPath = rolePath;
      }
      rows.push(renderRoleRow(role));
    }
    return rows;
  };

  // Render section header row
  const renderSectionHeader = (title: string, count: number) => (
    <tr key={`section-${title}`} className="bg-gray-100">
      <td colSpan={6} className="px-6 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          <Badge variant="default">{count}</Badge>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignments</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {/* Tenant Roles Section */}
          {groupedRoles.tenantRoles.length > 0 && (
            <>
              {renderSectionHeader("Tenant Roles", groupedRoles.tenantRoles.length)}
              {renderRolesWithPathHeaders(groupedRoles.tenantRoles)}
            </>
          )}

          {/* Application Roles Section */}
          {groupedRoles.applicationRoles.length > 0 && (
            <>
              {renderSectionHeader("Application Roles", groupedRoles.applicationRoles.length)}
              {renderRolesWithPathHeaders(groupedRoles.applicationRoles)}
            </>
          )}

          {/* App Domain Roles Section */}
          {groupedRoles.appDomainRoles.length > 0 && (
            <>
              {renderSectionHeader("App Domain Roles", groupedRoles.appDomainRoles.length)}
              {renderRolesWithPathHeaders(groupedRoles.appDomainRoles)}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
